import { useState, useEffect, useCallback } from 'react'

export interface Task {
  id: string
  title: string
  description?: string
  completed: boolean
  attention: 'low' | 'medium' | 'high'
  dueDate?: string
  lifeSpace: 'personal' | 'work' | 'home' | 'all'
  category?: string
  tags?: string[]
  subtasks?: Subtask[]
  comments?: Comment[]
  createdAt: Date
  updatedAt: Date
  deletedAt?: Date
}

export interface Subtask {
  id: string
  title: string
  completed: boolean
  order: number
}

export interface Comment {
  id: string
  text: string
  createdAt: Date
  author?: string
}

interface TaskFilters {
  lifeSpace?: 'personal' | 'work' | 'home' | 'all'
  attention?: 'low' | 'medium' | 'high'
  completed?: boolean
  dueDate?: {
    from?: Date
    to?: Date
  }
  search?: string
  tags?: string[]
}

// Mock data for initial development
const mockTasks: Task[] = [
  {
    id: '1',
    title: 'Präsentation fertigstellen',
    description: 'Die Quartalspräsentation für das Management Meeting vorbereiten. Fokus auf KPIs und Zukunftsplanung.',
    completed: false,
    attention: 'high',
    dueDate: '2025-02-15',
    lifeSpace: 'work',
    category: 'Meetings',
    tags: ['wichtig', 'Q1'],
    subtasks: [
      { id: '1-1', title: 'Folien erstellen', completed: true, order: 1 },
      { id: '1-2', title: 'Daten sammeln', completed: true, order: 2 },
      { id: '1-3', title: 'Review mit Team', completed: false, order: 3 },
      { id: '1-4', title: 'Finale Überarbeitung', completed: false, order: 4 }
    ],
    comments: [
      { id: 'c1', text: 'Deadline wurde um eine Woche verschoben', createdAt: new Date('2025-01-28'), author: 'System' }
    ],
    createdAt: new Date('2025-01-20'),
    updatedAt: new Date('2025-01-28')
  },
  {
    id: '2',
    title: 'Yoga-Kurs buchen',
    description: 'Neuen Yoga-Kurs für Februar finden und buchen',
    completed: false,
    attention: 'low',
    dueDate: '2025-02-01',
    lifeSpace: 'personal',
    category: 'Gesundheit',
    tags: ['wellness', 'sport'],
    subtasks: [],
    createdAt: new Date('2025-01-25'),
    updatedAt: new Date('2025-01-25')
  },
  {
    id: '3',
    title: 'Wocheneinkauf erledigen',
    description: 'Einkaufsliste abarbeiten und Vorräte auffüllen',
    completed: false,
    attention: 'medium',
    dueDate: '2025-01-31',
    lifeSpace: 'home',
    category: 'Haushalt',
    tags: ['routine'],
    subtasks: [
      { id: '3-1', title: 'Einkaufsliste erstellen', completed: true, order: 1 },
      { id: '3-2', title: 'Zum Supermarkt fahren', completed: false, order: 2 },
      { id: '3-3', title: 'Vorräte einräumen', completed: false, order: 3 }
    ],
    createdAt: new Date('2025-01-29'),
    updatedAt: new Date('2025-01-29')
  },
  {
    id: '4',
    title: 'Code Review durchführen',
    description: 'PR #234 reviewen und Feedback geben',
    completed: true,
    attention: 'high',
    dueDate: '2025-01-29',
    lifeSpace: 'work',
    category: 'Development',
    tags: ['code-review', 'team'],
    subtasks: [],
    createdAt: new Date('2025-01-27'),
    updatedAt: new Date('2025-01-29')
  },
  {
    id: '5',
    title: 'Geburtstagsgeschenk besorgen',
    description: 'Geschenk für Marias Geburtstag am 10. Februar',
    completed: false,
    attention: 'medium',
    dueDate: '2025-02-08',
    lifeSpace: 'personal',
    category: 'Soziales',
    tags: ['geburtstag', 'freunde'],
    subtasks: [
      { id: '5-1', title: 'Geschenkideen sammeln', completed: true, order: 1 },
      { id: '5-2', title: 'Geschenk kaufen', completed: false, order: 2 },
      { id: '5-3', title: 'Geschenk verpacken', completed: false, order: 3 }
    ],
    createdAt: new Date('2025-01-26'),
    updatedAt: new Date('2025-01-27')
  }
]

export const useTasks = (filters?: TaskFilters) => {
  const [tasks, setTasks] = useState<Task[]>(mockTasks)
  const [selectedTask, setSelectedTask] = useState<Task | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Filter tasks based on criteria
  const filteredTasks = useCallback(() => {
    let result = [...tasks]

    if (filters?.lifeSpace && filters.lifeSpace !== 'all') {
      result = result.filter(task => task.lifeSpace === filters.lifeSpace)
    }

    if (filters?.attention) {
      result = result.filter(task => task.attention === filters.attention)
    }

    if (filters?.completed !== undefined) {
      result = result.filter(task => task.completed === filters.completed)
    }

    if (filters?.search) {
      const searchLower = filters.search.toLowerCase()
      result = result.filter(task =>
        task.title.toLowerCase().includes(searchLower) ||
        task.description?.toLowerCase().includes(searchLower) ||
        task.tags?.some(tag => tag.toLowerCase().includes(searchLower))
      )
    }

    if (filters?.tags && filters.tags.length > 0) {
      result = result.filter(task =>
        task.tags?.some(tag => filters.tags?.includes(tag))
      )
    }

    if (filters?.dueDate) {
      result = result.filter(task => {
        if (!task.dueDate) return false
        const taskDate = new Date(task.dueDate)
        if (filters.dueDate?.from && taskDate < filters.dueDate.from) return false
        if (filters.dueDate?.to && taskDate > filters.dueDate.to) return false
        return true
      })
    }

    // Filter out soft-deleted tasks
    result = result.filter(task => !task.deletedAt)

    return result
  }, [tasks, filters])

  // Create a new task
  const createTask = useCallback(async (taskData: Partial<Task>) => {
    setLoading(true)
    try {
      const newTask: Task = {
        id: Date.now().toString(),
        title: taskData.title || 'Neue Aufgabe',
        completed: false,
        attention: taskData.attention || 'medium',
        lifeSpace: taskData.lifeSpace || 'personal',
        subtasks: [],
        comments: [],
        createdAt: new Date(),
        updatedAt: new Date(),
        ...taskData
      }

      setTasks(prev => [...prev, newTask])
      setError(null)
      return newTask
    } catch (err) {
      setError('Fehler beim Erstellen der Aufgabe')
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  // Update an existing task
  const updateTask = useCallback(async (taskId: string, updates: Partial<Task>) => {
    setLoading(true)
    try {
      setTasks(prev => prev.map(task =>
        task.id === taskId
          ? { ...task, ...updates, updatedAt: new Date() }
          : task
      ))

      // Update selected task if it's the one being updated
      if (selectedTask?.id === taskId) {
        setSelectedTask(prev => prev ? { ...prev, ...updates, updatedAt: new Date() } : null)
      }

      setError(null)
    } catch (err) {
      setError('Fehler beim Aktualisieren der Aufgabe')
      throw err
    } finally {
      setLoading(false)
    }
  }, [selectedTask])

  // Delete a task (soft delete)
  const deleteTask = useCallback(async (taskId: string) => {
    setLoading(true)
    try {
      setTasks(prev => prev.map(task =>
        task.id === taskId
          ? { ...task, deletedAt: new Date(), updatedAt: new Date() }
          : task
      ))

      // Clear selection if deleted task was selected
      if (selectedTask?.id === taskId) {
        setSelectedTask(null)
      }

      setError(null)
    } catch (err) {
      setError('Fehler beim Löschen der Aufgabe')
      throw err
    } finally {
      setLoading(false)
    }
  }, [selectedTask])

  // Toggle task completion
  const toggleTaskComplete = useCallback(async (taskId: string) => {
    const task = tasks.find(t => t.id === taskId)
    if (task) {
      await updateTask(taskId, { completed: !task.completed })
    }
  }, [tasks, updateTask])

  // Toggle subtask completion
  const toggleSubtaskComplete = useCallback(async (taskId: string, subtaskId: string) => {
    const task = tasks.find(t => t.id === taskId)
    if (task && task.subtasks) {
      const updatedSubtasks = task.subtasks.map(st =>
        st.id === subtaskId ? { ...st, completed: !st.completed } : st
      )
      await updateTask(taskId, { subtasks: updatedSubtasks })
    }
  }, [tasks, updateTask])

  // Add a comment to a task
  const addComment = useCallback(async (taskId: string, text: string) => {
    const task = tasks.find(t => t.id === taskId)
    if (task) {
      const newComment: Comment = {
        id: Date.now().toString(),
        text,
        createdAt: new Date(),
        author: 'User'
      }
      const updatedComments = [...(task.comments || []), newComment]
      await updateTask(taskId, { comments: updatedComments })
    }
  }, [tasks, updateTask])

  // Get tasks for today
  const getTodayTasks = useCallback(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)

    return filteredTasks().filter(task => {
      if (!task.dueDate) return false
      const taskDate = new Date(task.dueDate)
      return taskDate >= today && taskDate < tomorrow
    })
  }, [filteredTasks])

  // Get tasks for this week
  const getWeekTasks = useCallback(() => {
    const today = new Date()
    const startOfWeek = new Date(today)
    startOfWeek.setDate(today.getDate() - today.getDay())
    startOfWeek.setHours(0, 0, 0, 0)

    const endOfWeek = new Date(startOfWeek)
    endOfWeek.setDate(startOfWeek.getDate() + 7)

    return filteredTasks().filter(task => {
      if (!task.dueDate) return false
      const taskDate = new Date(task.dueDate)
      return taskDate >= startOfWeek && taskDate < endOfWeek
    })
  }, [filteredTasks])

  // Get overdue tasks
  const getOverdueTasks = useCallback(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    return filteredTasks().filter(task => {
      if (!task.dueDate || task.completed) return false
      const taskDate = new Date(task.dueDate)
      return taskDate < today
    })
  }, [filteredTasks])

  return {
    tasks: filteredTasks(),
    selectedTask,
    loading,
    error,
    createTask,
    updateTask,
    deleteTask,
    toggleTaskComplete,
    toggleSubtaskComplete,
    addComment,
    setSelectedTask,
    getTodayTasks,
    getWeekTasks,
    getOverdueTasks
  }
}